// src/pages/Generate.jsx
import React, { useRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductStore } from "../store/useProductStore";
import {
  Image,
  Video,
  Sparkles,
  Music2,
  Scissors,
  Wand2,
  SunMedium,
  ZoomIn,
  Expand,
  Replace as ReplaceIcon,
  Palette,
} from "lucide-react";
import placeholderImage from "../assets/placeholder.jpg";

/* -------------------- Media map: /public/media -------------------- */
const cardMedia = {
  // Ads
  productAvatarVideo: { type: "video", src: "/media/product-avatar-video.mp4" },
  adMarketingVideo: { type: "video", src: "/media/avatar-explainer-video.mp4" },
  productAvatarImage: { type: "image", src: "/media/product-avatar-image.webp" },
  photoshootAnyshoot: { type: "video", src: "/media/product-photoshoot.mp4" },
  photoshoot: { type: "video", src: "/media/product-background-changer.mp4" },
  adCreatives: { type: "video", src: "/media/ad-creative-image.mp4" },

  // Improvements
  removeBackground: { type: "video", src: "/media/remove-image-background.mp4" },
  colorRestoration: { type: "video", src: "/media/color-restoration.mp4" },
  enhancer: { type: "video", src: "/media/enhancer.mp4" },
  outpaint: { type: "video", src: "/media/extend-image-outpaint.mp4" },
  lightFix: { type: "video", src: "/media/light-fix.mp4" },
  objectReplacer: { type: "video", src: "/media/object-replacer.mp4" },
  objectRemover: { type: "video", src: "/media/object-remover.mp4" }, // ✅ NEW
  oldPhotosRestoration: { type: "video", src: "/media/old-photos-restoration.mp4" },
  upscaler: { type: "video", src: "/media/upscaler.mp4" },
};

/* -------------------- Actions (same routes/icons) -------------------- */
const actions = [
  // Ads
  {
    id: "productAvatarVideo",
    title: "Product Avatar Video",
    description:
      "Generate videos of an avatar holding and talking about your product, with AI-generated scripts and captions.",
    route: "/generate/product-avatar-video",
    icon: <Video size={20} />,
  },
  {
    id: "adMarketingVideo",
    title: "Avatar Explainer Video",
    description:
      "Create videos with AI avatars explaining your product, complete with captions and product image highlights.",
    route: "/generate/ad-marketing-video",
    icon: <Music2 size={20} />,
  },
  {
    id: "productAvatarImage",
    title: "Product Avatar Image",
    description:
      "Create hyper-realistic images of AI avatars holding your product. Customize avatar style, gender, and more.",
    route: "/generate/product-avatar-image",
    icon: <Image size={20} />,
  },
  {
    id: "photoshootAnyshoot",
    title: "Product Photoshoot",
    description:
      "Modify your product image with AI scenes and placements. Place your product anywhere in the photo for creative marketing.",
    route: "/generate/photoshoot-anyshoot",
    icon: <Image size={20} />,
  },
  {
    id: "photoshoot",
    title: "Product Background Changer",
    description:
      "Instantly enhance your product images by changing their backgrounds to suit any marketing scenario.",
    route: "/generate/background-replacer",
    icon: <Image size={20} />,
  },
  {
    id: "adCreatives",
    title: "Ad Creative Image (Coming soon - similar to Canva)",
    description:
      "Design stunning ad creatives with your product image, Canva-style overlays, and promo banners.",
    route: "/generate/ad-creatives",
    icon: <Sparkles size={20} />,
    disabled: true,
  },

  // Improvements
  {
    id: "removeBackground",
    title: "Remove Image Background",
    description: "Automatically remove backgrounds from product images using AI.",
    route: "/generate/remove-bg",
    icon: <Scissors size={20} />,
  },
  {
    id: "colorRestoration",
    title: "Color Restoration",
    description:
      "Restore or rebalance faded or incorrect colors in your images while preserving natural tones.",
    route: "/generate/color-restoration",
    icon: <Palette size={20} />,
  },
  {
    id: "enhancer",
    title: "Enhancer",
    description:
      "Improve clarity and detail—sharpen, reduce noise, and boost overall image quality.",
    route: "/generate/enhancer",
    icon: <Wand2 size={20} />,
  },
  {
    id: "outpaint",
    title: "Extend Image (Outpaint)",
    description:
      "Extend your image beyond its borders to wider or taller compositions without cropping.",
    route: "/generate/outpaint",
    icon: <Expand size={20} />,
  },
  {
    id: "lightFix",
    title: "Light Fix",
    description:
      "Correct exposure and lighting issues for balanced highlights, shadows, and midtones.",
    route: "/generate/light-fix",
    icon: <SunMedium size={20} />,
  },
  {
    id: "objectReplacer",
    title: "Object Replacer",
    description:
      "Select and replace objects via prompt-driven editing while keeping the scene consistent.",
    route: "/generate/object-replacer",
    icon: <ReplaceIcon size={20} />,
  },
  {
    id: "objectRemover",
    title: "Object Remover",
    description:
      "Paint a mask over unwanted areas and remove objects cleanly while preserving the background.",
    route: "/generate/object-remover", // ✅ NEW
    icon: <Scissors size={20} />, // keeping simple; can switch to a custom icon later
  },
  {
    id: "oldPhotosRestoration",
    title: "Old Photos Restoration",
    description:
      "Repair scratches, noise, and color fade in old photos—revive vintage memories with AI.",
    route: "/generate/old-photos-restoration",
    icon: <Sparkles size={20} />,
  },
  {
    id: "upscaler",
    title: "Upscaler",
    description:
      "Increase image resolution while preserving sharpness and detail with AI super-resolution.",
    route: "/generate/upscaler",
    icon: <ZoomIn size={20} />,
  },
];

/* -------------------- Sections layout -------------------- */
const sections = [
  {
    title: "Most used",
    groups: [
      { title: "Video Ads", ids: ["productAvatarVideo", "adMarketingVideo"] },
      {
        title: "Image Ads",
        ids: ["productAvatarImage", "photoshootAnyshoot", "photoshoot", "adCreatives"],
      },
    ],
  },
  {
    title: "General image improvements",
    ids: [
      "removeBackground",
      "colorRestoration",
      "enhancer",
      "outpaint",
      "lightFix",
      "objectReplacer",
      "objectRemover", // ✅ NEW
      "oldPhotosRestoration",
      "upscaler",
    ],
  },
];

/* -------------------- Card preview (~238 × 176) -------------------- */
/**
 * Lazy-load preview:
 * - Images: normal <img> with loading="lazy"
 * - Videos: only render <video> once the card is in viewport
 *   (keeps autoplay but avoids loading all videos on first paint)
 */
function CardPreview({ media }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Observe visibility to delay video mount until needed
  useEffect(() => {
    if (!containerRef.current || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  // Autoplay video when it mounts & is visible
  useEffect(() => {
    if (media?.type !== "video" || !videoRef.current || !isVisible) return;
    const v = videoRef.current;
    const playPromise = v.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // Ignore autoplay block errors
      });
    }
  }, [media, isVisible]);

  return (
    <div className="w-full flex justify-start" ref={containerRef}>
      <div
        className="relative bg-[#0f0f20] rounded-2xl overflow-hidden border border-[#2A2A40] w-[238px] h-[176px]"
        style={{ boxShadow: "inset 0 0 0 1px rgba(42,42,64,0.18)" }}
      >
        {!media ? (
          <img
            src={placeholderImage}
            alt="Preview"
            className="w-full h-full object-cover"
            draggable={false}
            loading="lazy"
          />
        ) : media.type === "image" ? (
          <img
            src={media.src}
            alt="Preview"
            className="w-full h-full object-cover"
            draggable={false}
            loading="lazy"
          />
        ) : (
          <>
            {/* Until visible, don't mount the video element at all */}
            {isVisible ? (
              <video
                ref={videoRef}
                src={media.src}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                autoPlay
                preload="none"
              />
            ) : (
              <img
                src={placeholderImage}
                alt="Preview loading"
                className="w-full h-full object-cover"
                draggable={false}
                loading="lazy"
              />
            )}
          </>
        )}
        <div className="absolute inset-0 bg-transparent group-hover:bg-black/10 transition" />
      </div>
    </div>
  );
}

/* -------------------- Page -------------------- */
export default function Generate() {
  const navigate = useNavigate();
  const { selectedProduct } = useProductStore();

  const actionsById = useMemo(() => {
    const map = {};
    for (const a of actions) map[a.id] = a;
    return map;
  }, []);

  const handleActionClick = (action) => {
    if (action?.disabled) return;
  
    if (selectedProduct?.id && action.route.startsWith("/generate/")) {
      navigate(`${action.route}/${selectedProduct.id}`);
    } else {
      navigate(action.route);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10 shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 transition-all duration-300 hover:text-pink-500 hover:drop-shadow-[0_0_10px_rgba(255,0,128,0.5)]">
          AI-Generated Asset Selection
        </h1>
        <p className="text-white/70 mb-6 sm:mb-8">
          Select the asset type you would like to generate using our Artificial Intelligence.
        </p>

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.title} className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-semibold text-white">{section.title}</h2>
              </div>

              {"groups" in section ? (
                <div className="space-y-8">
                  {section.groups.map((group) => (
                    <div key={group.title}>
                      <div className="text-sm text-white/70 mb-3">{group.title}</div>

                      {/* Dense grid like the reference */}
                      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                        {group.ids.map((id) => {
                          const a = actionsById[id];
                          if (!a) return null;
                          const media = cardMedia[id];
                          return (
<div
  key={id}
  onClick={() => handleActionClick(a)}
  className={`group relative bg-[#1C1C2E] border border-[#2A2A40] text-white rounded-2xl p-4 transition transform ${
    a.disabled
      ? "opacity-50 grayscale cursor-not-allowed pointer-events-none"
      : "cursor-pointer hover:scale-[1.01] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30"
  }`}
>
  <CardPreview media={media} />
  <div className="flex items-center gap-3 mt-3 mb-1">
    <div
      className={`bg-[#2A2A40] w-8 h-8 flex items-center justify-center rounded-full transition-transform ${
        a.disabled ? "" : "group-hover:scale-110"
      }`}
      title={a.title}
    >
      {a.icon}
    </div>
    <h3 className="text-sm font-semibold leading-tight">{a.title}</h3>
  </div>
  <p className="text-xs text-white/70 leading-snug">{a.description}</p>
</div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Flat section (General image improvements)
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                  {section.ids.map((id) => {
                    const a = actionsById[id];
                    if (!a) return null;
                    const media = cardMedia[id];
                    return (
<div
  key={id}
  onClick={() => handleActionClick(a)}
  className={`group relative bg-[#1C1C2E] border border-[#2A2A40] text-white rounded-2xl p-4 transition transform ${
    a.disabled
      ? "opacity-50 grayscale cursor-not-allowed pointer-events-none"
      : "cursor-pointer hover:scale-[1.01] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30"
  }`}
>
  <CardPreview media={media} />
  <div className="flex items-center gap-3 mt-3 mb-1">
    <div
      className={`bg-[#2A2A40] w-8 h-8 flex items-center justify-center rounded-full transition-transform ${
        a.disabled ? "" : "group-hover:scale-110"
      }`}
      title={a.title}
    >
      {a.icon}
    </div>
    <h3 className="text-sm font-semibold leading-tight">{a.title}</h3>
  </div>
  <p className="text-xs text-white/70 leading-snug">{a.description}</p>
</div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
